import React, { useState, useEffect, useCallback } from 'react';
import useFeedback from '../hooks/useFeedback';
import {
  Box, Button, Paper, Typography, Alert, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Stack, IconButton, Tooltip, Collapse, Divider,
  CircularProgress, Checkbox, FormControlLabel,
} from '@mui/material';
import {
  PlayArrow, CheckCircle, Cancel, Refresh, ExpandMore, ExpandLess,
  Visibility, Add, Delete, Assignment, Download, Edit, InfoOutlined,
} from '@mui/icons-material';
import {
  getPayrollRuns, getPayrollRun, createPayrollRun, updatePayrollRun,
  calculatePayrollRun, approvePayrollRun, processPayrollRun,
  voidPayrollRun, deletePayrollRun, getEligiblePayPeriods,
  getAvailableEntries, getStubPreview, downloadPayStubAdmin, getPayrollAutomationStatus,
} from '../api';
import { useConfirmDialog } from './ConfirmDialog';
import { useFeatures } from '../context/FeaturesContext';
import { isPayrollReadOnly, PAYROLL_READ_ONLY_TEXT } from '../utils/featureFlags';

const STATUS_COLORS = {
  draft: 'default',
  calculating: 'info',
  review: 'warning',
  approved: 'info',
  processed: 'success',
  voided: 'error',
};

// Plan 068: why a reimbursement listed on a calculated run can no longer be paid
const CONFLICT_LABELS = { paid: 'already paid', not_approved: 'no longer approved', missing: 'deleted', amount_changed: 'amount changed' };

// Gitea #148: payroll_items.calculation_notes carries {"minimum_wage_shortfall": {...}} (minimumWageService).
const minimumWageShortfall = (item) => {
  try {
    const n = typeof item.calculation_notes === 'string' ? JSON.parse(item.calculation_notes) : item.calculation_notes;
    const s = n && n.minimum_wage_shortfall;
    return s && Array.isArray(s.weeks) && s.weeks.length > 0 ? s : null;
  } catch (e) { return null; }
};
const fmtYmd = (s) => { const [y, m, d] = String(s).slice(0, 10).split('-'); return `${+m}/${+d}/${y}`; };

const fmt = (v) => `$${parseFloat(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

export default function Payroll() {
  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();
  // Payroll off + payroll history: past runs and stubs stay viewable, but every
  // write (create, calculate, approve, process, void, delete, edit) is hidden —
  // the server refuses them with FEATURE_DISABLED anyway.
  const { features } = useFeatures();
  const readOnly = isPayrollReadOnly(features);
  const [runs, setRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [lastAutoFailure, setLastAutoFailure] = useState(null);
  const [eligiblePeriods, setEligiblePeriods] = useState([]);
  const [error, setError] = useState('');
  const [success, showSuccess] = useFeedback(3000);
  const [loading, setLoading] = useState(false);
  const [createDialog, setCreateDialog] = useState(false);
  const [voidDialog, setVoidDialog] = useState({ open: false, runId: null });
  const [voidReason, setVoidReason] = useState('');
  const [selectedPeriodId, setSelectedPeriodId] = useState('');
  const [checkDate, setCheckDate] = useState('');
  const [expandedItems, setExpandedItems] = useState({});
  const [availableEntries, setAvailableEntries] = useState(null);
  const [selectedEntryIds, setSelectedEntryIds] = useState(new Set());
  const [entryPayOverrides, setEntryPayOverrides] = useState({});
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [stubPreviewData, setStubPreviewData] = useState(null);
  const [stubPreviewItemId, setStubPreviewItemId] = useState(null);
  const [stubPreviewLoading, setStubPreviewLoading] = useState(false);
  const [editingCheckDate, setEditingCheckDate] = useState(false);
  const [editCheckDateValue, setEditCheckDateValue] = useState('');

  const handleSaveCheckDate = async (runId) => {
    try {
      setLoading(true);
      const upd = await updatePayrollRun(runId, { check_date: editCheckDateValue || null });
      setEditingCheckDate(false);
      if (upd.data?.recalculation_required) {
        setError(`Paycheck date moved this run from tax year ${upd.data.tax_year_from} to ${upd.data.tax_year_to}. It is back in draft: click Select Entries & Calculate before approving.`);
      } else {
        showSuccess('Paycheck date updated');
      }
      try {
        const res = await getPayrollRun(runId);
        setSelectedRun(res.data);
        await loadRuns();
      } catch (reloadErr) {
        // The update itself succeeded; do not replace its message with a failure
        console.error('Failed to reload payroll run after paycheck date update:', reloadErr);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update paycheck date');
    } finally {
      setLoading(false);
    }
  };

  const loadRuns = useCallback(async () => {
    try {
      const res = await getPayrollRuns();
      setRuns(res.data);
    } catch (err) {
      setError('Failed to load payroll runs');
    }
    // Last automatic-processing problem (plan 085). 403 for users without
    // can_run_payroll just means no indicator.
    try {
      const st = await getPayrollAutomationStatus();
      setLastAutoFailure(st.data?.last_failure || null);
    } catch (err) {
      setLastAutoFailure(null);
    }
  }, []);

  useEffect(() => { loadRuns(); }, [loadRuns]);

  const loadEligiblePeriods = async () => {
    try {
      const res = await getEligiblePayPeriods();
      setEligiblePeriods(res.data);
    } catch (err) {
      setError('Failed to load eligible pay periods');
    }
  };

  const handleCreate = async () => {
    if (!selectedPeriodId) return;
    setLoading(true);
    try {
      const res = await createPayrollRun({ pay_period_id: selectedPeriodId, check_date: checkDate || undefined });
      setCreateDialog(false);
      setSelectedPeriodId('');
      setCheckDate('');
      showSuccess('Payroll run created — select entries and calculate');
      // handleViewRun will auto-load entries for draft runs
      await handleViewRun(res.data.id);
      loadRuns();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create payroll run');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadEntries = async (runId) => {
    setEntriesLoading(true);
    try {
      const res = await getAvailableEntries(runId);
      console.log('[Payroll] Available entries:', res.data.employees?.length, 'employees,',
        res.data.employees?.reduce((s, e) => s + e.entries.length, 0), 'entries');
      setAvailableEntries(res.data);
      // Default: only current-period entries selected (prior entries unchecked)
      const currentIds = new Set();
      res.data.employees.forEach(emp =>
        emp.entries.filter(e => e.period === 'current').forEach(e => currentIds.add(e.id))
      );
      setSelectedEntryIds(currentIds);
      setEntryPayOverrides({});
    } catch (err) {
      console.error('[Payroll] handleLoadEntries error:', err);
      setError(err.response?.data?.error || 'Failed to load available entries');
    } finally {
      setEntriesLoading(false);
    }
  };

  const handleCalculateWithSelection = async (runId) => {
    setLoading(true);
    try {
      // Always send explicit selection — empty array means "no entries"
      const entryIds = Array.from(selectedEntryIds);
      // Only include overrides for selected entries, converting strings to numbers
      const overrides = {};
      for (const [id, rawValue] of Object.entries(entryPayOverrides)) {
        if (selectedEntryIds.has(Number(id))) {
          const num = parseFloat(rawValue);
          if (!isNaN(num) && num >= 0) overrides[id] = num;
        }
      }
      console.log('[Payroll] Calculate with', entryIds.length, 'selected entries,', Object.keys(overrides).length, 'overrides');
      await calculatePayrollRun(runId, entryIds, overrides);
      showSuccess('Payroll calculated');
      setAvailableEntries(null);
      setSelectedEntryIds(new Set());
      setEntryPayOverrides({});
      // Load run without re-triggering entry picker
      const res = await getPayrollRun(runId);
      setSelectedRun(res.data);
      await loadRuns();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to calculate payroll');
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculate = async (runId) => {
    // Load entries for re-selection, then user clicks Calculate
    await handleLoadEntries(runId);
  };

  const handleApprove = async (runId) => {
    setLoading(true);
    try {
      await approvePayrollRun(runId);
      showSuccess('Payroll approved');
      loadRuns();
      handleViewRun(runId);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to approve payroll');
    } finally {
      setLoading(false);
    }
  };

  const handleProcess = async (runId) => {
    setLoading(true);
    try {
      await processPayrollRun(runId);
      showSuccess('Payroll processed — entries marked as paid');
      loadRuns();
      handleViewRun(runId);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to process payroll');
    } finally {
      setLoading(false);
    }
  };

  const handleVoid = async () => {
    if (!voidReason.trim()) return;
    setLoading(true);
    try {
      await voidPayrollRun(voidDialog.runId, voidReason);
      setVoidDialog({ open: false, runId: null });
      setVoidReason('');
      showSuccess('Payroll run voided');
      setSelectedRun(null);
      loadRuns();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to void payroll');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVoided = async (runId) => {
    const confirmed = await showConfirm({
      title: 'Delete Voided Run',
      message: 'Permanently delete this voided payroll run?',
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'warning'
    });
    if (!confirmed) return;
    setLoading(true);
    try {
      await deletePayrollRun(runId);
      showSuccess('Voided payroll run deleted');
      setSelectedRun(null);
      loadRuns();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete payroll run');
    } finally {
      setLoading(false);
    }
  };

  const handleViewRun = async (runId) => {
    try {
      const res = await getPayrollRun(runId);
      console.log('[Payroll] handleViewRun', runId, 'status=', res.data.status);
      setSelectedRun(res.data);
      setStubPreviewData(null);
      setStubPreviewItemId(null);
      // Auto-load entries only for draft runs (review runs show results, not picker)
      if (res.data.status === 'draft') {
        console.log('[Payroll] Loading entries for draft run', runId);
        await handleLoadEntries(runId);
      } else {
        setAvailableEntries(null);
      }
    } catch (err) {
      console.error('[Payroll] handleViewRun error:', err);
      setError('Failed to load payroll run details');
    }
  };

  const handleStubPreview = async (runId, itemId) => {
    if (stubPreviewItemId === itemId) {
      // Toggle off
      setStubPreviewData(null);
      setStubPreviewItemId(null);
      return;
    }
    setStubPreviewLoading(true);
    setStubPreviewItemId(itemId);
    try {
      const res = await getStubPreview(runId, itemId);
      setStubPreviewData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load stub preview');
      setStubPreviewItemId(null);
    } finally {
      setStubPreviewLoading(false);
    }
  };

  const openCreateDialog = async () => {
    await loadEligiblePeriods();
    setCreateDialog(true);
  };

  const toggleItemExpand = (itemId) => {
    setExpandedItems(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  // ── Run Detail View ─────────────────────────────────────────────
  const renderRunDetail = () => {
    if (!selectedRun) return null;
    const r = selectedRun;
    const mwFlagged = (r.items || []).filter(it => minimumWageShortfall(it));

    return (
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" component="div">
              {r.pay_period_name || `${fmtDate(r.start_date)} — ${fmtDate(r.end_date)}`}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              {editingCheckDate ? (
                <>
                  <TextField
                    type="date" size="small" value={editCheckDateValue}
                    onChange={(e) => setEditCheckDateValue(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    label="Paycheck Date"
                    sx={{ width: 180 }}
                  />
                  <Button size="small" variant="contained" onClick={() => handleSaveCheckDate(r.id)} disabled={loading}>Save</Button>
                  <Button size="small" onClick={() => setEditingCheckDate(false)}>Cancel</Button>
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Paycheck: {r.check_date ? fmtDate(r.check_date) : 'Not set'}
                  </Typography>
                  {!readOnly && ['draft', 'review'].includes(r.status) && (
                    <IconButton size="small" onClick={() => { setEditCheckDateValue(r.check_date ? new Date(r.check_date).toISOString().split('T')[0] : ''); setEditingCheckDate(true); }}>
                      <Edit fontSize="small" />
                    </IconButton>
                  )}
                </>
              )}
              <Chip label={r.status} color={STATUS_COLORS[r.status]} size="small" />
            </Box>
          </Box>
          {!readOnly && (
          <Stack direction="row" spacing={1}>
            {(r.status === 'draft' || r.status === 'review') && !availableEntries && (
              <Button startIcon={<Refresh />} onClick={() => handleRecalculate(r.id)} disabled={loading} size="small">
                {r.status === 'draft' ? 'Select Entries & Calculate' : 'Recalculate'}
              </Button>
            )}
            {(r.status === 'draft' || r.status === 'review') && availableEntries && (
              <Button startIcon={<PlayArrow />} variant="contained" onClick={() => handleCalculateWithSelection(r.id)} disabled={loading} size="small">
                Calculate{selectedEntryIds.size > 0 ? ` (${selectedEntryIds.size} entries)` : ''}
              </Button>
            )}
            {r.status === 'review' && (
              <>
                <Button startIcon={<CheckCircle />} color="success" variant="contained"
                  onClick={() => handleApprove(r.id)} disabled={loading || !r.check_date || mwFlagged.length > 0} size="small">
                  Approve
                </Button>
                {!r.check_date && (
                  <Tooltip title="Set the Paycheck Date first (pencil icon next to Paycheck). It sets the tax year for the run.">
                    <InfoOutlined fontSize="small" color="action" tabIndex={0} aria-label="Set the Paycheck Date first (pencil icon next to Paycheck). It sets the tax year for the run." sx={{ alignSelf: 'center' }} />
                  </Tooltip>
                )}
                {mwFlagged.length > 0 && (
                  <Tooltip title="Approve is off: someone in this run is paid below the minimum wage. See the red box below.">
                    <InfoOutlined fontSize="small" color="action" tabIndex={0} aria-label="Approve is off: someone in this run is paid below the minimum wage. See the red box below." sx={{ alignSelf: 'center' }} />
                  </Tooltip>
                )}
              </>
            )}
            {r.status === 'approved' && (
              <Button startIcon={<PlayArrow />} color="primary" variant="contained"
                onClick={() => handleProcess(r.id)} disabled={loading} size="small">
                Process
              </Button>
            )}
            {r.status !== 'voided' && (
              <Button startIcon={<Cancel />} color="error" size="small"
                onClick={() => setVoidDialog({ open: true, runId: r.id })} disabled={loading}>
                Void
              </Button>
            )}
            {r.status === 'voided' && (
              <Button startIcon={<Delete />} color="error" size="small"
                onClick={() => handleDeleteVoided(r.id)} disabled={loading}>
                Delete
              </Button>
            )}
          </Stack>
          )}
        </Box>

        {/* Summary Row */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <SummaryCard label="Employees" value={r.employee_count} />
          <SummaryCard label="Gross" value={fmt(r.total_gross)} />
          <SummaryCard label="Pre-Tax Ded." value={fmt(r.total_pre_tax_deductions)} />
          <SummaryCard label="Taxes" value={fmt(r.total_taxes)} />
          <SummaryCard label="Post-Tax Ded." value={fmt(r.total_post_tax_deductions)} />
          <SummaryCard label="Net Pay" value={fmt(r.total_net)} highlight />
        </Box>

        {r.status === 'draft' && (r.items || []).length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This run's paycheck date moved it into a different tax year, so the amounts below are out of date.
            Click Select Entries &amp; Calculate before approving.
          </Alert>
        )}

        {/* Entry Picker */}
        {availableEntries && (r.status === 'draft' || r.status === 'review') && (
          <Box sx={{ mb: 2 }}>
            <EntryPicker
              data={availableEntries}
              selectedIds={selectedEntryIds}
              onSelectionChange={setSelectedEntryIds}
              payOverrides={entryPayOverrides}
              onOverrideChange={setEntryPayOverrides}
              loading={entriesLoading}
            />
          </Box>
        )}

        {/* Plan 068: reimbursements this run can no longer pay — Process will be refused */}
        {['review', 'approved'].includes(r.status)
          && (r.items || []).some(it => (it.reimbursement_conflicts || []).length > 0) && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              Some expense reimbursements in this run can no longer be paid
            </Typography>
            <Typography variant="body2">
              {(r.items || []).flatMap(it => (it.reimbursement_conflicts || []).map(c =>
                `${it.first_name} ${it.last_name}: ${c.name} ${fmt(c.amount)} (${CONFLICT_LABELS[c.reason] || c.reason})`
              )).join('; ')}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {r.status === 'review'
                ? 'Click Recalculate to drop them, then review the new totals.'
                : 'Process will be refused and nothing will be paid. Void this run, create a new run for the pay period and calculate it again.'}
            </Typography>
          </Alert>
        )}

        {/* Gitea #148: hourly pay below the minimum wage — Approve/Process are refused */}
        {['review', 'approved'].includes(r.status) && mwFlagged.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              Pay below minimum wage
              <Tooltip title={`HourTrax checks each workweek (Sunday to Saturday): the straight-time pay for hours worked must be at least the federal minimum wage ($7.25, 29 U.S.C. § 206) or the state minimum for the person's Work State (State if Work State is blank), whichever is higher. State rates as of ${minimumWageShortfall(mwFlagged[0]).data_as_of}; where a state has regional rates the lowest is used. Weeks that start or end outside this pay period are not checked here. Tipped cash wages, youth and training wages, state small-employer rates (e.g. Ohio) and § 14(c) certificate rates are not supported: anyone paid that way is flagged and the run cannot be approved. Leave their entries out of the run (Recalculate, then untick them) and pay them outside HourTrax. City and county minimum wages are not checked: confirm those yourself. Pay is averaged over the Sunday–Saturday workweek (not allowed in California, where each hour must be paid at least the minimum wage).`}>
                <InfoOutlined tabIndex={0} aria-label="Pay below minimum wage" sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />
              </Tooltip>
            </Typography>
            {mwFlagged.flatMap(it => minimumWageShortfall(it).weeks.map(w => (
              <Typography variant="body2" key={`${it.employee_id}-${w.week_start}`}>
                {`${it.first_name} ${it.last_name}: week of ${fmtYmd(w.week_start)}, ${w.hours} hrs at ${fmt(w.rate)}/hr, minimum ${fmt(w.floor)} (${w.floor_basis === 'state' ? (minimumWageShortfall(it).state || 'state') : 'federal'})`}
              </Typography>
            )))}
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {r.status === 'review'
                ? "Approve is refused until this is fixed. Someone who can edit pay rates (Admin, Payroll Admin, or a role with View pay rates) sets the person's Hourly Rate on the People tab, or the entry's Hourly Rate on Timesheets if the entries were saved at a low rate. Then click Recalculate. Fix it before the Paycheck Date. If the correct rate cannot be set in time, click Recalculate and leave this person's entries out so everyone else is paid on schedule, then pay this person at least the minimum wage for those hours by the same payday. If the state is wrong, set Work State on People and Recalculate."
                : "Process is refused and nothing will be paid. Fix the rate, then void this run, create a new run for the pay period and calculate it again. If the correct rate cannot be set before the Paycheck Date, leave this person's entries out of the new run so everyone else is paid on schedule, then pay this person at least the minimum wage for those hours by the same payday. If the state is wrong, set Work State on People first."}
            </Typography>
          </Alert>
        )}

        {/* Employee Items */}
        {r.items && r.items.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell sx={{ fontWeight: 'bold' }}>Employee</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Hours</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Gross</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Taxes</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Deductions</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Net Pay</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {r.items.map(item => (
                  <React.Fragment key={item.id}>
                    <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggleItemExpand(item.id)}>
                      <TableCell sx={{ width: 30, p: 0.5 }}>
                        <IconButton size="small">
                          {expandedItems[item.id] ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {item.first_name} {item.last_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.employee_type} · {fmt(item.hourly_rate)}/hr
                        </Typography>
                        {minimumWageShortfall(item) && <Chip size="small" color="error" label="Below minimum wage" sx={{ ml: 1 }} />}
                      </TableCell>
                      <TableCell align="right">
                        {parseFloat(item.total_hours).toFixed(2)}
                        {parseFloat(item.overtime_hours) > 0 && (
                          <Typography variant="caption" color="warning.main" display="block">
                            {parseFloat(item.overtime_hours).toFixed(2)} {item.overtime_enabled ? 'OT' : 'Bonus'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{fmt(item.gross_pay)}</TableCell>
                      <TableCell align="right">{fmt(item.tax_total)}</TableCell>
                      <TableCell align="right">
                        {fmt(parseFloat(item.pre_tax_total || 0) + parseFloat(item.post_tax_total || 0))}
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold">{fmt(item.net_pay)}</Typography>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Preview Pay Stub">
                            <IconButton size="small" onClick={() => handleStubPreview(r.id, item.id)}
                              color={stubPreviewItemId === item.id ? 'primary' : 'default'}>
                              {stubPreviewLoading && stubPreviewItemId === item.id
                                ? <CircularProgress size={18} /> : <Assignment />}
                            </IconButton>
                          </Tooltip>
                          {item.pay_stub_id && (
                            <Tooltip title="Download PDF">
                              <IconButton size="small" onClick={() => {
                                downloadPayStubAdmin(item.pay_stub_id).catch(err =>
                                  setError(err.message || 'Failed to download pay stub'));
                              }}>
                                <Download />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                        <Collapse in={expandedItems[item.id]}>
                          <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                            <ItemBreakdown item={item} />
                          </Box>
                        </Collapse>
                        <Collapse in={stubPreviewItemId === item.id && !!stubPreviewData}>
                          <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                            {stubPreviewData && stubPreviewItemId === item.id && (
                              <StubPreviewPanel data={stubPreviewData} />
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    );
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Payroll</Typography>
        {!readOnly && (
          <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog}>
            Run Payroll
          </Button>
        )}
      </Box>

      {readOnly && (
        <Alert severity="info" sx={{ mb: 2, py: 0 }}>{PAYROLL_READ_ONLY_TEXT}</Alert>
      )}

      {lastAutoFailure && (
        <Alert severity="warning" sx={{ mb: 2 }}
          icon={<Tooltip title={lastAutoFailure.message}><InfoOutlined fontSize="inherit" /></Tooltip>}
          action={lastAutoFailure.run_id ? (
            <Button color="inherit" size="small" onClick={() => handleViewRun(lastAutoFailure.run_id)}>View run</Button>
          ) : null}>
          Last automatic run problem: run #{lastAutoFailure.run_id} ({lastAutoFailure.period_name}, check date {lastAutoFailure.check_date}) was not processed - {lastAutoFailure.message}. {lastAutoFailure.recovery}
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {selectedRun && renderRunDetail()}

      {/* Runs List */}
      <Typography variant="h6" sx={{ mb: 1 }}>Payroll History</Typography>
      {runs.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">No payroll runs yet. Lock a pay period, then click "Run Payroll".</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Pay Period</TableCell>
                <TableCell>Paycheck Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Employees</TableCell>
                <TableCell align="right">Gross</TableCell>
                <TableCell align="right">Net</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.map(run => (
                <TableRow key={run.id} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {run.pay_period_name || `${fmtDate(run.start_date)} — ${fmtDate(run.end_date)}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fmtDate(run.start_date)} — {fmtDate(run.end_date)}
                    </Typography>
                  </TableCell>
                  <TableCell>{run.check_date ? fmtDate(run.check_date) : fmtDate(run.run_date)}</TableCell>
                  <TableCell>
                    <Chip label={run.status} color={STATUS_COLORS[run.status]} size="small" />
                  </TableCell>
                  <TableCell align="right">{run.employee_count}</TableCell>
                  <TableCell align="right">{fmt(run.total_gross)}</TableCell>
                  <TableCell align="right">{fmt(run.total_net)}</TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => handleViewRun(run.id)}>
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Run Payroll</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a locked pay period to run payroll for. Only periods without an active payroll run are shown.
          </Typography>
          {eligiblePeriods.length === 0 ? (
            <Alert severity="info">No eligible pay periods. Lock a pay period first.</Alert>
          ) : (
            <>
              <TextField
                fullWidth select label="Pay Period" value={selectedPeriodId}
                onChange={(e) => setSelectedPeriodId(e.target.value)} sx={{ mt: 1 }}>
                {eligiblePeriods.map(pp => (
                  <MenuItem key={pp.id} value={pp.id}>
                    {pp.name || `${fmtDate(pp.start_date)} — ${fmtDate(pp.end_date)}`}
                    {' '}({fmtDate(pp.start_date)} — {fmtDate(pp.end_date)})
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth label="Paycheck Date" type="date" value={checkDate}
                onChange={(e) => setCheckDate(e.target.value)} sx={{ mt: 2 }}
                required
                InputLabelProps={{ shrink: true }}
                helperText="The date employees are paid. It sets the tax year: a December period paid in January is taxed and reported in January's year."
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}
            disabled={!selectedPeriodId || !checkDate || loading}>
            {loading ? <CircularProgress size={20} /> : 'Create Run'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Void Dialog */}
      <Dialog open={voidDialog.open} onClose={() => setVoidDialog({ open: false, runId: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Void Payroll Run</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Voiding will reverse all paid entries and revert the pay period to locked status.
          </Alert>
          <TextField
            fullWidth required label="Reason for voiding" value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)} multiline rows={2} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setVoidDialog({ open: false, runId: null }); setVoidReason(''); }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleVoid}
            disabled={!voidReason.trim() || loading}>
            {loading ? <CircularProgress size={20} /> : 'Void Run'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialogComponent />
    </Box>
  );
}

function SummaryCard({ label, value, highlight }) {
  return (
    <Box sx={{
      bgcolor: highlight ? 'primary.main' : 'action.hover',
      color: highlight ? 'primary.contrastText' : 'text.primary',
      borderRadius: 1, px: 2, py: 1, minWidth: 100,
    }}>
      <Typography variant="caption" sx={{ opacity: 0.8 }}>{label}</Typography>
      <Typography variant="h6" fontWeight="bold">{value}</Typography>
    </Box>
  );
}

function EntryPicker({ data, selectedIds, onSelectionChange, payOverrides, onOverrideChange, loading }) {
  if (loading) return <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress /></Box>;
  if (!data) return null;

  const toggleEntry = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    onSelectionChange(next);
  };

  const toggleAllForEmployee = (empEntries, checked) => {
    const next = new Set(selectedIds);
    empEntries.forEach(e => { if (checked) next.add(e.id); else next.delete(e.id); });
    onSelectionChange(next);
  };

  const toggleAll = (checked) => {
    if (checked) {
      // Add all current-period entries while preserving any prior-period selections
      const next = new Set(selectedIds);
      data.employees.forEach(emp => emp.entries.filter(e => e.period === 'current').forEach(e => next.add(e.id)));
      onSelectionChange(next);
    } else {
      // Remove only current-period entries, preserve prior-period selections
      const next = new Set(selectedIds);
      data.employees.forEach(emp => emp.entries.filter(e => e.period === 'current').forEach(e => next.delete(e.id)));
      onSelectionChange(next);
    }
  };

  const setOverride = (entryId, value) => {
    const next = { ...payOverrides };
    if (value === '' || value === null || value === undefined) {
      delete next[String(entryId)];
    } else {
      // Store raw string to allow typing decimals like "0." or "12."
      next[String(entryId)] = value;
    }
    onOverrideChange(next);
  };

  const currentEntryCount = data.employees.reduce((sum, emp) => sum + emp.entries.filter(e => e.period === 'current').length, 0);
  const totalEntries = data.employees.reduce((sum, emp) => sum + emp.entries.length, 0);
  const overrideCount = Object.keys(payOverrides).filter(id => selectedIds.has(Number(id))).length;
  const allCurrentChecked = currentEntryCount > 0 && data.employees.every(emp =>
    emp.entries.filter(e => e.period === 'current').every(e => selectedIds.has(e.id)));

  const renderEntryRow = (entry, isPrior) => {
    const entryTotalPay = parseFloat(entry.total_pay || 0);
    const paidAlready = parseFloat(entry.paid_amount || 0);
    const isPartiallyPaid = paidAlready > 0 && paidAlready < entryTotalPay;
    const overrideRaw = payOverrides[String(entry.id)];
    const hasOverride = overrideRaw !== undefined && overrideRaw !== '';
    const salaryCovered = entry.pay_basis === 'salary';

    return (
      <Box key={entry.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, opacity: isPrior ? 0.85 : 1 }}>
        <Checkbox checked={selectedIds.has(entry.id)}
          onChange={() => toggleEntry(entry.id)} size="small" />
        <Typography variant="body2" sx={{ minWidth: 90, fontStyle: isPrior ? 'italic' : 'normal' }}>
          {new Date(entry.entry_date).toLocaleDateString()}
        </Typography>
        <Typography variant="body2" sx={{ minWidth: 70 }}>
          {parseFloat(entry.hours_worked).toFixed(2)} hrs
        </Typography>
        <Typography variant="body2" sx={{ minWidth: 65 }}>
          {fmt(entryTotalPay)}
        </Typography>
        {parseFloat(entry.bonus_amount || 0) > 0 && (
          <Typography variant="body2" color="success.main" sx={{ minWidth: 80 }}>
            +{fmt(entry.bonus_amount)}
          </Typography>
        )}
        <Chip label={entry.entry_type || 'regular'} size="small" variant="outlined"
          color={entry.entry_type === 'bonus' ? 'warning' : entry.entry_type === 'overtime' ? 'info' : entry.entry_type === 'holiday' ? 'success' : 'default'}
          sx={{ height: 20, fontSize: '0.7rem' }} />
        {salaryCovered && (
          <>
            <Chip label="Salary-covered" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
            <Tooltip title="Covered by the salary: claimed by this run and marked paid, but adds nothing to gross pay. Shown on the pay stub as Salary-covered hours (reference). To pay it, change the entry's Pay Basis to Hourly."><InfoOutlined fontSize="small" color="action" /></Tooltip>
          </>
        )}
        {isPrior && (
          <Chip label="prior period" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'warning.light' }} />
        )}
        {isPartiallyPaid && (
          <Chip label={`${fmt(paidAlready)} paid`} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'info.light' }} />
        )}
        {/* Partial pay override input */}
        <TextField
          size="small"
          type="number"
          placeholder="Pay amt"
          disabled={salaryCovered}
          value={overrideRaw !== undefined ? overrideRaw : ''}
          onChange={(e) => setOverride(entry.id, e.target.value)}
          inputProps={{ min: 0, max: entryTotalPay, step: 0.01 }}
          sx={{
            width: 100,
            '& .MuiInputBase-input': { py: 0.3, px: 0.5, fontSize: '0.8rem' },
          }}
        />
        {hasOverride && (
          <Chip label="partial" size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />
        )}
        {(entry.description || entry.work_details) && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 150 }}>
            {entry.description || entry.work_details}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          Select Entries for Payroll
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {overrideCount > 0 && (
            <Typography variant="caption" color="warning.main">
              {overrideCount} partial pay override{overrideCount > 1 ? 's' : ''}
            </Typography>
          )}
          <FormControlLabel
            control={<Checkbox checked={allCurrentChecked} indeterminate={selectedIds.size > 0 && !allCurrentChecked}
              onChange={(e) => toggleAll(e.target.checked)} size="small" />}
            label={<Typography variant="body2">Select All Current ({selectedIds.size}/{totalEntries})</Typography>}
          />
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
        Pay Period: {new Date(data.payPeriod.start).toLocaleDateString()} — {new Date(data.payPeriod.end).toLocaleDateString()}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
        Use "Pay amt" to set a partial pay amount for an entry. Leave blank for full pay.
      </Typography>
      {data.employees.map(emp => {
        const currentEntries = emp.entries.filter(e => e.period === 'current');
        const priorEntries = emp.entries.filter(e => e.period === 'prior');
        const empSelected = emp.entries.filter(e => selectedIds.has(e.id)).length;
        const empAllChecked = emp.entries.length > 0 && empSelected === emp.entries.length;
        return (
          <Box key={emp.id} sx={{ mb: 2, pl: 1, borderLeft: '3px solid', borderColor: 'primary.light' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              {emp.entries.length > 0 && (
                <Checkbox checked={empAllChecked}
                  indeterminate={empSelected > 0 && !empAllChecked}
                  onChange={(e) => toggleAllForEmployee(emp.entries, e.target.checked)}
                  size="small" />
              )}
              <Typography variant="subtitle2">{emp.name}</Typography>
              <Chip label={emp.employee_type} size="small" variant="outlined" />
            </Box>
            {emp.employee_type === 'salary' && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
                Base salary included automatically
              </Typography>
            )}
            {emp.entries.length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
                {emp.employee_type === 'salary' ? 'No bonus/extra entries' : 'No time entries in this period'}
              </Typography>
            ) : (
              <Box sx={{ ml: 4 }}>
                {currentEntries.map(entry => renderEntryRow(entry, false))}
                {priorEntries.length > 0 && (
                  <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                    <Typography variant="caption" color="warning.main" sx={{ mb: 0.5, display: 'block', fontWeight: 'bold' }}>
                      Prior Unpaid Entries ({priorEntries.length})
                    </Typography>
                    {priorEntries.map(entry => renderEntryRow(entry, true))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        );
      })}
    </Paper>
  );
}

function StubPreviewPanel({ data }) {
  if (!data) return null;
  const d = data;
  const cellSx = { py: 0.3, px: 1, fontSize: '0.8rem', fontFamily: 'monospace' };
  const headerSx = { ...cellSx, fontWeight: 'bold', bgcolor: 'grey.200' };

  return (
    <Paper variant="outlined" sx={{ p: 2, fontFamily: 'monospace', fontSize: '0.85rem', maxWidth: 800 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, pb: 1, borderBottom: '2px solid #333' }}>
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" fontFamily="monospace">{d.company?.name}</Typography>
          <Typography variant="caption" fontFamily="monospace" display="block">{d.company?.address}</Typography>
          <Typography variant="caption" fontFamily="monospace" display="block">
            {d.company?.city}, {d.company?.state} {d.company?.zip}
          </Typography>
          {d.company?.ein && <Typography variant="caption" fontFamily="monospace">EIN: {d.company?.ein}</Typography>}
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="subtitle2" fontFamily="monospace">{d.employee?.name}</Typography>
          {d.employee?.ssn_last_four && (
            <Typography variant="caption" fontFamily="monospace" display="block">SSN: ***-**-{d.employee?.ssn_last_four}</Typography>
          )}
          <Typography variant="caption" fontFamily="monospace" display="block">{d.employee?.address}</Typography>
          <Typography variant="caption" fontFamily="monospace" display="block">
            {d.employee?.city}, {d.employee?.state} {d.employee?.zip}
          </Typography>
        </Box>
      </Box>

      {/* Pay Period */}
      <Box sx={{ mb: 2, textAlign: 'center' }}>
        <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
          Pay Period: {fmtDate(d.payPeriod?.start)} — {fmtDate(d.payPeriod?.end)}
          {d.payPeriod?.payDay && ` | Pay Day: ${fmtDate(d.payPeriod?.payDay)}`}
        </Typography>
      </Box>

      {/* Earnings */}
      <TableContainer sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headerSx}>Earnings</TableCell>
              <TableCell align="right" sx={headerSx}>Rate</TableCell>
              <TableCell align="right" sx={headerSx}>Hours</TableCell>
              <TableCell align="right" sx={headerSx}>Current</TableCell>
              <TableCell align="right" sx={headerSx}>YTD</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(d.earnings || []).map((e, i) => (
              <TableRow key={i}>
                <TableCell sx={cellSx}>{e.description}</TableCell>
                <TableCell align="right" sx={cellSx}>{e.rate ? fmt(e.rate) : '—'}</TableCell>
                <TableCell align="right" sx={cellSx}>{e.hours || '—'}</TableCell>
                <TableCell align="right" sx={cellSx}>{fmt(e.current)}</TableCell>
                <TableCell align="right" sx={cellSx}>{e.ytd ? fmt(e.ytd) : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Taxes side-by-side */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        {/* Employee Taxes */}
        <Box sx={{ flex: 1, minWidth: 280 }}>
          <Typography variant="subtitle2" fontFamily="monospace" fontWeight="bold" sx={{ mb: 0.5 }}>
            Employee Taxes Withheld
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerSx}>Tax</TableCell>
                <TableCell align="right" sx={headerSx}>Current</TableCell>
                <TableCell align="right" sx={headerSx}>YTD</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {d.employeeTaxes?.federal != null && (
                <TableRow><TableCell sx={cellSx}>Federal Income Tax</TableCell><TableCell align="right" sx={cellSx}>{fmt(d.employeeTaxes.federal)}</TableCell><TableCell align="right" sx={cellSx}>{fmt(d.employeeTaxes.federal_ytd)}</TableCell></TableRow>
              )}
              {d.employeeTaxes?.social_security != null && (
                <TableRow><TableCell sx={cellSx}>Social Security</TableCell><TableCell align="right" sx={cellSx}>{fmt(d.employeeTaxes.social_security)}</TableCell><TableCell align="right" sx={cellSx}>{fmt(d.employeeTaxes.social_security_ytd)}</TableCell></TableRow>
              )}
              {d.employeeTaxes?.medicare != null && (
                <TableRow><TableCell sx={cellSx}>Medicare</TableCell><TableCell align="right" sx={cellSx}>{fmt(d.employeeTaxes.medicare)}</TableCell><TableCell align="right" sx={cellSx}>{fmt(d.employeeTaxes.medicare_ytd)}</TableCell></TableRow>
              )}
              {d.employeeTaxes?.state != null && parseFloat(d.employeeTaxes.state) > 0 && (
                <TableRow><TableCell sx={cellSx}>{d.employeeTaxes.state_code || 'State'} Withholding Tax</TableCell><TableCell align="right" sx={cellSx}>{fmt(d.employeeTaxes.state)}</TableCell><TableCell align="right" sx={cellSx}>{fmt(d.employeeTaxes.state_ytd)}</TableCell></TableRow>
              )}
              {d.employeeTaxes?.local_eit > 0 && (
                <TableRow><TableCell sx={cellSx}>{d.employeeTaxes.local_jurisdiction || 'Local'} EIT</TableCell><TableCell align="right" sx={cellSx}>{fmt(d.employeeTaxes.local_eit)}</TableCell><TableCell align="right" sx={cellSx}>{fmt(d.employeeTaxes.local_eit_ytd)}</TableCell></TableRow>
              )}
              {d.employeeTaxes?.local_lst > 0 && (
                <TableRow><TableCell sx={cellSx}>{d.employeeTaxes.local_jurisdiction || 'Local'} LST</TableCell><TableCell align="right" sx={cellSx}>{fmt(d.employeeTaxes.local_lst)}</TableCell><TableCell align="right" sx={cellSx}>{fmt(d.employeeTaxes.local_lst_ytd)}</TableCell></TableRow>
              )}
              {d.employeeTaxes?.pa_uc_employee > 0 && (
                <TableRow><TableCell sx={cellSx}>PA UC Tax Employee Withholding</TableCell><TableCell align="right" sx={cellSx}>{fmt(d.employeeTaxes.pa_uc_employee)}</TableCell><TableCell align="right" sx={cellSx}>{fmt(d.employeeTaxes.pa_uc_employee_ytd)}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        {/* Employer Taxes */}
        <Box sx={{ flex: 1, minWidth: 280 }}>
          <Typography variant="subtitle2" fontFamily="monospace" fontWeight="bold" sx={{ mb: 0.5 }}>
            Employer Tax
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerSx}>Tax</TableCell>
                <TableCell align="right" sx={headerSx}>Current</TableCell>
                <TableCell align="right" sx={headerSx}>YTD</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {d.employerTaxes?.social_security != null && (
                <TableRow><TableCell sx={cellSx}>Social Security</TableCell><TableCell align="right" sx={cellSx}>{fmt(d.employerTaxes.social_security)}</TableCell><TableCell align="right" sx={cellSx}>{fmt(d.employerTaxes.social_security_ytd)}</TableCell></TableRow>
              )}
              {d.employerTaxes?.medicare != null && (
                <TableRow><TableCell sx={cellSx}>Medicare</TableCell><TableCell align="right" sx={cellSx}>{fmt(d.employerTaxes.medicare)}</TableCell><TableCell align="right" sx={cellSx}>{fmt(d.employerTaxes.medicare_ytd)}</TableCell></TableRow>
              )}
              {d.employerTaxes?.futa != null && parseFloat(d.employerTaxes.futa) > 0 && (
                <TableRow><TableCell sx={cellSx}>FUTA</TableCell><TableCell align="right" sx={cellSx}>{fmt(d.employerTaxes.futa)}</TableCell><TableCell align="right" sx={cellSx}>{fmt(d.employerTaxes.futa_ytd)}</TableCell></TableRow>
              )}
              {d.employerTaxes?.state_unemployment != null && parseFloat(d.employerTaxes.state_unemployment) > 0 && (
                <TableRow><TableCell sx={cellSx}>{d.employerTaxes.state_code || 'State'} UC Tax</TableCell><TableCell align="right" sx={cellSx}>{fmt(d.employerTaxes.state_unemployment)}</TableCell><TableCell align="right" sx={cellSx}>{fmt(d.employerTaxes.state_unemployment_ytd)}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Box>

      {/* Employee Deductions */}
      {((d.deductions?.preTax?.length || 0) > 0 || (d.deductions?.postTax?.length || 0) > 0) && (
        <TableContainer sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontFamily="monospace" fontWeight="bold" sx={{ mb: 0.5 }}>
            Employee Deductions
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerSx}>Description</TableCell>
                <TableCell sx={headerSx}>Type</TableCell>
                <TableCell align="right" sx={headerSx}>Current</TableCell>
                <TableCell align="right" sx={headerSx}>YTD</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(d.deductions?.preTax || []).map((ded, i) => (
                <TableRow key={`pre-${i}`}>
                  <TableCell sx={cellSx}>{ded.name}</TableCell>
                  <TableCell sx={cellSx}>{ded.category || 'Pre-Tax'}</TableCell>
                  <TableCell align="right" sx={cellSx}>{fmt(ded.amount)}</TableCell>
                  <TableCell align="right" sx={cellSx}>{ded.ytd ? fmt(ded.ytd) : '—'}</TableCell>
                </TableRow>
              ))}
              {(d.deductions?.postTax || []).map((ded, i) => (
                <TableRow key={`post-${i}`}>
                  <TableCell sx={cellSx}>{ded.name}</TableCell>
                  <TableCell sx={cellSx}>{ded.category || 'Post-Tax'}</TableCell>
                  <TableCell align="right" sx={cellSx}>{fmt(ded.amount)}</TableCell>
                  <TableCell align="right" sx={cellSx}>{ded.ytd ? fmt(ded.ytd) : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Employer Contributions */}
      {(d.employerContributions?.length || 0) > 0 && (
        <TableContainer sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontFamily="monospace" fontWeight="bold" sx={{ mb: 0.5 }}>
            Employer Contributions
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerSx}>Description</TableCell>
                <TableCell sx={headerSx}>Type</TableCell>
                <TableCell align="right" sx={headerSx}>Current</TableCell>
                <TableCell align="right" sx={headerSx}>YTD</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(d.employerContributions || []).map((c, i) => (
                <TableRow key={i}>
                  <TableCell sx={cellSx}>{c.name}</TableCell>
                  <TableCell sx={cellSx}>{c.category || ''}</TableCell>
                  <TableCell align="right" sx={cellSx}>{fmt(c.amount)}</TableCell>
                  <TableCell align="right" sx={cellSx}>{c.ytd ? fmt(c.ytd) : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Summary */}
      <Box sx={{ borderTop: '2px solid #333', pt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headerSx}>Summary</TableCell>
              <TableCell align="right" sx={headerSx}>Current</TableCell>
              <TableCell align="right" sx={headerSx}>YTD</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell sx={cellSx}>Gross Earnings</TableCell>
              <TableCell align="right" sx={cellSx}>{fmt(d.summary?.gross)}</TableCell>
              <TableCell align="right" sx={cellSx}>{fmt(d.ytd?.gross)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={cellSx}>Pre-Tax Deductions/Contributions</TableCell>
              <TableCell align="right" sx={cellSx}>{fmt(d.summary?.preTaxDed)}</TableCell>
              <TableCell align="right" sx={cellSx}>{fmt(d.ytd?.preTax)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={cellSx}>Taxes</TableCell>
              <TableCell align="right" sx={cellSx}>{fmt(d.summary?.taxes)}</TableCell>
              <TableCell align="right" sx={cellSx}>{fmt(d.ytd?.taxes)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={cellSx}>Post-Tax Deductions/Contributions</TableCell>
              <TableCell align="right" sx={cellSx}>{fmt(d.summary?.postTaxDed)}</TableCell>
              <TableCell align="right" sx={cellSx}>{fmt(d.ytd?.postTax)}</TableCell>
            </TableRow>
            {parseFloat(d.summary?.imputedIncome || 0) > 0 && (
              <TableRow>
                <TableCell sx={cellSx}>Imputed Pay</TableCell>
                <TableCell align="right" sx={cellSx}>{fmt(d.summary?.imputedIncome)}</TableCell>
                <TableCell align="right" sx={cellSx}>{fmt(d.ytd?.imputedIncome)}</TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell sx={{ ...cellSx, fontWeight: 'bold', fontSize: '0.95rem', borderTop: '2px solid #333' }}>Net Pay</TableCell>
              <TableCell align="right" sx={{ ...cellSx, fontWeight: 'bold', fontSize: '0.95rem', borderTop: '2px solid #333' }}>{fmt(d.summary?.netPay)}</TableCell>
              <TableCell align="right" sx={{ ...cellSx, fontWeight: 'bold', fontSize: '0.95rem', borderTop: '2px solid #333' }}>{fmt(d.ytd?.net)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={cellSx}>Total Reimbursements</TableCell>
              <TableCell align="right" sx={cellSx}>{fmt(d.summary?.reimbursements)}</TableCell>
              <TableCell align="right" sx={cellSx}>{fmt(0)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ ...cellSx, fontWeight: 'bold', borderTop: '1px solid #ccc' }}>Check Amount</TableCell>
              <TableCell align="right" sx={{ ...cellSx, fontWeight: 'bold', borderTop: '1px solid #ccc' }}>{fmt(d.summary?.checkAmount)}</TableCell>
              <TableCell align="right" sx={{ ...cellSx, fontWeight: 'bold', borderTop: '1px solid #ccc' }}>{fmt(d.ytd?.checkAmount)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={cellSx}>Total Hours Worked</TableCell>
              <TableCell align="right" sx={cellSx}>{parseFloat(d.summary?.totalHours || 0).toFixed(2)}</TableCell>
              <TableCell align="right" sx={cellSx}>{parseFloat(d.summary?.totalHours || 0).toFixed(2)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>

      {/* Time Off Policy */}
      {d.pto && (parseFloat(d.pto.used || 0) > 0 || parseFloat(d.pto.accrued || 0) > 0 || parseFloat(d.pto.balance || 0) !== 0) && (
        <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid #ccc' }}>
          <Typography variant="subtitle2" fontFamily="monospace" fontWeight="bold" sx={{ mb: 0.5 }}>
            Time Off Policy
          </Typography>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={cellSx}>Hours used this period</TableCell>
                <TableCell align="right" sx={cellSx}>{parseFloat(d.pto.used || 0).toFixed(2)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={cellSx}>Hours accrued this period</TableCell>
                <TableCell align="right" sx={cellSx}>{parseFloat(d.pto.accrued || 0) > 0 ? `+${parseFloat(d.pto.accrued).toFixed(2)}` : '0.00'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ ...cellSx, fontWeight: 'bold' }}>Remaining Time Off Balance</TableCell>
                <TableCell align="right" sx={{ ...cellSx, fontWeight: 'bold' }}>{parseFloat(d.pto.balance || 0).toFixed(2)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
}

function ItemBreakdown({ item }) {
  const taxes = typeof item.taxes === 'string' ? JSON.parse(item.taxes) : (item.taxes || {});
  const preTax = typeof item.pre_tax_deductions === 'string'
    ? JSON.parse(item.pre_tax_deductions) : (item.pre_tax_deductions || []);
  const postTax = typeof item.post_tax_deductions === 'string'
    ? JSON.parse(item.post_tax_deductions) : (item.post_tax_deductions || []);
  const employerTaxes = typeof item.employer_taxes === 'string'
    ? JSON.parse(item.employer_taxes) : (item.employer_taxes || {});
  const employerContributions = typeof item.employer_contributions === 'string'
    ? JSON.parse(item.employer_contributions) : (item.employer_contributions || []);
  const reimbursements = typeof item.reimbursements === 'string'
    ? JSON.parse(item.reimbursements) : (item.reimbursements || []);

  return (
    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
      {/* Earnings */}
      <Box sx={{ minWidth: 200 }}>
        <Typography variant="subtitle2" gutterBottom>Earnings</Typography>
        {item.employee_type === 'salary' ? (
          <Typography variant="body2">Salaried: {fmt(item.regular_pay)}</Typography>
        ) : (
          <Typography variant="body2">Regular: {parseFloat(item.regular_hours).toFixed(2)} hrs × {fmt(parseFloat(item.regular_hours) > 0 ? parseFloat(item.regular_pay) / parseFloat(item.regular_hours) : parseFloat(item.hourly_rate))} = {fmt(item.regular_pay)}</Typography>
        )}
        {item.employee_type === 'salary' && parseFloat(item.salary_covered_hours || 0) > 0 && (
          <Typography variant="body2" color="text.secondary">
            Salary-covered hours (reference): {parseFloat(item.salary_covered_hours).toFixed(2)} hrs = {fmt(0)}
            <Tooltip title="Hours on Salary-covered entries (for example recurring contract hours). The salary already pays for them, so they add nothing to gross pay."><InfoOutlined sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} color="action" /></Tooltip>
          </Typography>
        )}
        {parseFloat(item.overtime_hours) > 0 && (
          <Typography variant="body2">
            {/* Rate shown is derived (pay ÷ hours) — entries carry snapshotted
                rates, so the employee's current rate may not reconcile */}
            {item.overtime_enabled ? 'Overtime' : 'Bonus Hours'}: {parseFloat(item.overtime_hours).toFixed(2)} hrs × {fmt(parseFloat(item.overtime_pay) / parseFloat(item.overtime_hours))} = {fmt(item.overtime_pay)}
          </Typography>
        )}
        {parseFloat(item.time_off_hours || 0) > 0 && (
          <Typography variant="body2">Time Off: {parseFloat(item.time_off_hours).toFixed(2)} hrs = {fmt(item.time_off_pay)}</Typography>
        )}
        {parseFloat(item.bonus_amount || 0) > 0 && (
          <Typography variant="body2">Bonus: {fmt(item.bonus_amount)}</Typography>
        )}
        <Divider sx={{ my: 0.5 }} />
        <Typography variant="body2" fontWeight="bold">Gross: {fmt(item.gross_pay)}</Typography>
      </Box>

      {/* Pre-Tax Deductions */}
      {preTax.length > 0 && (
        <Box sx={{ minWidth: 180 }}>
          <Typography variant="subtitle2" gutterBottom>Pre-Tax Deductions</Typography>
          {preTax.map((d, i) => (
            <Typography key={i} variant="body2">{d.name}: {fmt(d.amount)}</Typography>
          ))}
          <Divider sx={{ my: 0.5 }} />
          <Typography variant="body2" fontWeight="bold">Total: {fmt(item.pre_tax_total)}</Typography>
        </Box>
      )}

      {/* Employee Taxes */}
      <Box sx={{ minWidth: 180 }}>
        <Typography variant="subtitle2" gutterBottom>Employee Taxes</Typography>
        {taxes.federal_income != null && <Typography variant="body2">Federal: {fmt(taxes.federal_income)}</Typography>}
        {taxes.social_security != null && <Typography variant="body2">Social Security: {fmt(taxes.social_security)}</Typography>}
        {taxes.medicare != null && <Typography variant="body2">Medicare: {fmt(taxes.medicare)}</Typography>}
        {taxes.state_income != null && parseFloat(taxes.state_income) > 0 && (
          <Typography variant="body2">State: {fmt(taxes.state_income)}</Typography>
        )}
        <Divider sx={{ my: 0.5 }} />
        <Typography variant="body2" fontWeight="bold">Total: {fmt(item.tax_total)}</Typography>
      </Box>

      {/* Employer Taxes */}
      {parseFloat(item.employer_tax_total || 0) > 0 && (
        <Box sx={{ minWidth: 180 }}>
          <Typography variant="subtitle2" gutterBottom>Employer Taxes</Typography>
          {employerTaxes.employer_social_security != null && (
            <Typography variant="body2">Social Security: {fmt(employerTaxes.employer_social_security)}</Typography>
          )}
          {employerTaxes.employer_medicare != null && (
            <Typography variant="body2">Medicare: {fmt(employerTaxes.employer_medicare)}</Typography>
          )}
          {employerTaxes.futa != null && parseFloat(employerTaxes.futa) > 0 && (
            <Typography variant="body2">FUTA: {fmt(employerTaxes.futa)}</Typography>
          )}
          {employerTaxes.state_unemployment != null && parseFloat(employerTaxes.state_unemployment) > 0 && (
            <Typography variant="body2">State UC: {fmt(employerTaxes.state_unemployment)}</Typography>
          )}
          <Divider sx={{ my: 0.5 }} />
          <Typography variant="body2" fontWeight="bold">Total: {fmt(item.employer_tax_total)}</Typography>
        </Box>
      )}

      {/* Post-Tax Deductions */}
      {postTax.length > 0 && (
        <Box sx={{ minWidth: 180 }}>
          <Typography variant="subtitle2" gutterBottom>Post-Tax Deductions</Typography>
          {postTax.map((d, i) => (
            <Typography key={i} variant="body2">{d.name}: {fmt(d.amount)}</Typography>
          ))}
          <Divider sx={{ my: 0.5 }} />
          <Typography variant="body2" fontWeight="bold">Total: {fmt(item.post_tax_total)}</Typography>
        </Box>
      )}

      {/* Employer Contributions */}
      {employerContributions.length > 0 && (
        <Box sx={{ minWidth: 180 }}>
          <Typography variant="subtitle2" gutterBottom>Employer Contributions</Typography>
          {employerContributions.map((c, i) => (
            <Typography key={i} variant="body2">{c.name}: {fmt(c.amount)}</Typography>
          ))}
          <Divider sx={{ my: 0.5 }} />
          <Typography variant="body2" fontWeight="bold">Total: {fmt(item.employer_contribution_total)}</Typography>
        </Box>
      )}

      {/* Reimbursements */}
      {reimbursements.length > 0 && (
        <Box sx={{ minWidth: 180 }}>
          <Typography variant="subtitle2" gutterBottom>Reimbursements</Typography>
          {reimbursements.map((r, i) => {
            const conflict = (item.reimbursement_conflicts || []).find(c => c.id === r.id);
            return (
              <Typography key={i} variant="body2" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {r.name || r.description || 'Expense'}: {fmt(r.amount)}
                {conflict && (
                  <Tooltip title="This expense changed after the run was calculated (ticked Paid by hand, reversed, deleted, amount edited, or paid by another run), so this run cannot pay it. Recalculate a run in review; void and re-run an approved one.">
                    <Chip size="small" color="warning" label={CONFLICT_LABELS[conflict.reason] || conflict.reason} />
                  </Tooltip>
                )}
              </Typography>
            );
          })}
          <Divider sx={{ my: 0.5 }} />
          <Typography variant="body2" fontWeight="bold">Total: {fmt(item.reimbursement_total)}</Typography>
        </Box>
      )}

      {/* PTO */}
      {(parseFloat(item.pto_hours_accrued || 0) > 0 || parseFloat(item.pto_hours_used || 0) > 0) && (
        <Box sx={{ minWidth: 150 }}>
          <Typography variant="subtitle2" gutterBottom>PTO</Typography>
          {parseFloat(item.pto_hours_used || 0) > 0 && (
            <Typography variant="body2">Used: {parseFloat(item.pto_hours_used).toFixed(2)} hrs</Typography>
          )}
          {parseFloat(item.pto_hours_accrued || 0) > 0 && (
            <Typography variant="body2">Accrued: {parseFloat(item.pto_hours_accrued).toFixed(2)} hrs</Typography>
          )}
          <Tooltip title="Balance after this run's accrual, as of when the run was processed (as of calculation while it is in review). Hours used were deducted when each request was approved, so they are not subtracted again here.">
            <Typography variant="body2">Balance: {parseFloat(item.pto_balance || 0).toFixed(2)} hrs</Typography>
          </Tooltip>
        </Box>
      )}

      {/* YTD */}
      <Box sx={{ minWidth: 150 }}>
        <Typography variant="subtitle2" gutterBottom>YTD Summary</Typography>
        <Typography variant="body2">Gross: {fmt(item.ytd_gross)}</Typography>
        <Typography variant="body2">Taxes: {fmt(item.ytd_taxes)}</Typography>
        <Typography variant="body2">Net: {fmt(item.ytd_net)}</Typography>
      </Box>
    </Box>
  );
}
